// Bankkártyák lista (billing, BELSŐ nézet) — a KÖZÖS DataTable-re épül (a billing-profiles a referencia-minta):
// szerver-oldali (BFF) kereső (300 ms debounce; birtokos/bank/utolsó4) + státusz-facet (érvényes/lejár/lejárt) +
// darabszám-meta a toolbarban, szerver-rendezés (birtokos / bank / lejárat / ügyfél), végtelen görgetés (100/lap).
// A /payment-cards BFF-végpontról fogyaszt (busz-sorok + ügyfél-név feloldva; tenant-scope grant-fal RLS Odin-
// oldalon). Page-gate: `payment_cards.view`.
//
// A státusz (érvényes / hamarosan lejár / lejárt) TISZTÁN a lejáratból (expiryYear/Month) SZÁMÍTOTT — nincs hozzá
// backend-mező; a facet ezért a MÁR BETÖLTÖTT sorokon szűr (kliens-oldali).
//
// Írás-vertikum: create/edit FormSheet (payment_cards.manage) + törlés (payment_cards.delete). NINCS elsődleges
// (kártyának nincs elsődlegese) — a billing-profiles set-primary action itt kimarad.
import { type PaymentCardRowSchema } from '@atlas/contracts';
import { PlusIcon } from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PaymentCardFormSheet } from '@/components/payment-cards/payment-card-form-sheet';
import { PaymentCardRowActions } from '@/components/payment-cards/payment-card-row-actions';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { Text } from '@/components/ui/text';
import { paymentCardKeys } from '@/features/payment-cards/api';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// A SZŰRŐK search-paramok (a state-konvenció: a szűrt nézet legyen linkelhető és F5-biztos) — a route-nak
// ismernie kell a kulcsokat, különben a router kiszűri őket. A default-értékű kulcs nem kerül az URL-be.
const paymentCardsSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  sort: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/payment-cards')({
  validateSearch: (search) => paymentCardsSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: PaymentCardsPage,
});

type PaymentCardRow = z.infer<typeof PaymentCardRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.paymentCards.query>>;

const PAGE_SIZE = 100;
const ALL = '__all__';

type CardStatus = 'valid' | 'expiring' | 'expired';

/** A kártya státusza a lejáratból SZÁMÍTVA (nincs backend-mező): lejárt < aktuális hónap; hamarosan lejár = az
 *  aktuális vagy a következő hónap; egyébként érvényes. Hiányzó lejárat → érvényesnek tekintjük (nincs jel). */
function cardStatus(card: PaymentCardRow, now: Date): CardStatus {
  if (card.expiryYear === null || card.expiryMonth === null) return 'valid';
  const cardYM = card.expiryYear * 12 + card.expiryMonth;
  const curYM = now.getFullYear() * 12 + (now.getMonth() + 1);
  if (cardYM < curYM) return 'expired';
  if (cardYM <= curYM + 1) return 'expiring';
  return 'valid';
}

/** Kártyatípus → kijelzett címke (rövid, nagybetűs). Ismeretlen → maga a kód. */
function brandLabel(brand: string | null): string {
  if (!brand) return '—';
  const map: Record<string, string> = {
    visa: 'VISA',
    mastercard: 'Mastercard',
    amex: 'Amex',
    maestro: 'Maestro',
    other: '•',
  };
  return map[brand] ?? brand;
}

function PaymentCardsPage(): JSX.Element {
  const { t } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('payment_cards.view');
  const canManage = can('payment_cards.manage');
  const canDelete = can('payment_cards.delete');

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    status: ALL,
    sort: 'holderName',
    order: 'asc',
  });
  const q = urlFilters.q;
  const statusFilter = urlFilters.status;
  const sort = urlFilters.sort;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);

  const [editTarget, setEditTarget] = useState<PaymentCardRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PaymentCardRow | null>(null);

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: payment_cards.view nélkül meg sem kérdezzük a szervert.
  const gated = !permsLoading && !canView;

  const filters = { q, sort, order };
  const listKey = paymentCardKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.paymentCards.query({
        query: {
          q: q || undefined,
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const allRows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.cards : [])),
    [pages],
  );

  // A státusz kliens-oldalon számított (nincs backend-mező) → a facet a már betöltött sorokon szűr.
  const now = useMemo(() => new Date(), []);
  const rows = useMemo(() => {
    if (statusFilter === ALL) return allRows;
    return allRows.filter((c) => cardStatus(c, now) === statusFilter);
  }, [allRows, statusFilter, now]);

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters = q !== '' || statusFilter !== ALL;

  // Oszlop-fejléc kattintás: ugyanaz az oszlop → irány-váltás; MÁS oszlop → az új oszlop, `asc`-ből.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort ? { order: order === 'asc' ? 'desc' : 'asc' } : { sort: col, order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista lista-sor eltávolítás + rollback): a soft-delete a BFF `/payment-cards/:id`.
  // A TÖRLÉS IS OCC-s: a szerver a sor verzióját várja `If-Match`-ben. Enélkül 428 — és nem véletlenül: a
  // szerver-oldali guardok (elsődleges profil · használatban lévő kártya · számlázott hozzárendelés) az
  // OLVASOTT állapotra szólnak, a törlés viszont később megy; a kettő között beérkező módosítás enélkül
  // elveszett volna.
  const removeMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>(
    {
      mutationFn: ({ id, version }) =>
        api.paymentCardDelete.mutation({
          params: { id },
          headers: { 'if-match': String(version) },
        }),
      invalidateKeys: [paymentCardKeys.lists()],
      optimistic: {
        queryKey: listKey,
        update: (old) => {
          const id = deleteTarget?.id;
          if (!old || !id) return old;
          return {
            ...old,
            pages: old.pages.map((p) =>
              p.status === 200
                ? {
                    ...p,
                    body: {
                      ...p.body,
                      cards: p.body.cards.filter((c) => c.id !== id),
                      total: Math.max(0, p.body.total - 1),
                    },
                  }
                : p,
            ),
          };
        },
      },
      messages: {
        success: t('toast:paymentCards.deleted'),
        // A TÖRLÉS 409-e a HASZNÁLAT-GUARD (a kártya egy szolgáltatás-hozzárendelés fizetési módja), nem OCC:
        // a törléshez nincs If-Match, tehát verzió-ütközés itt nem fordulhat elő.
        conflict: t('toast:paymentCards.inUse'),
        forbidden: t('toast:paymentCards.forbidden'),
        validation: t('toast:paymentCards.validation'),
        error: t('toast:paymentCards.error'),
      },
    },
  );

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate(
      { id: deleteTarget.id, version: deleteTarget.version },
      { onSettled: () => setDeleteTarget(null) },
    );
  }

  const emDash = <span className="text-muted-foreground">—</span>;
  /** Az em-dash SZÖVEGES alakja (a legördülő-elemekben nem JSX-et, hanem szöveget fűzünk). */
  const emDashText = '—';

  const columns: DataTableColumn<PaymentCardRow>[] = [
    {
      // 1. Maszkolt kártya (rendezhető birtokosra) — {BRAND} •••• {last4}, alatta halvány birtokos.
      id: 'holderName',
      header: t('paymentCards.columns.card'),
      sortable: true,
      cardLabel: t('paymentCards.columns.card'),
      width: 'minmax(0,2fr)',
      multiline: true,
      cell: (c) => (
        <div className="min-w-0 space-y-0.5">
          <Text variant="itemTitle" as="div" className="min-w-0 truncate tabular-nums">
            {brandLabel(c.brand)} •••• {c.last4 ?? '····'}
          </Text>
          {c.holderName && (
            <Text tone="tertiary" as="div" className="truncate">
              {c.holderName}
            </Text>
          )}
        </div>
      ),
    },
    {
      // 2. Bank (rendezhető) — halvány.
      id: 'bankName',
      card: 'subtitle',
      header: t('paymentCards.columns.bank'),
      sortable: true,
      cardLabel: t('paymentCards.columns.bank'),
      width: 'minmax(0,1.5fr)',
      className: 'text-muted-foreground',
      cell: (c) => (c.bankName ? <span className="truncate">{c.bankName}</span> : emDash),
    },
    {
      // 3. Lejárat (rendezhető évre) — YYYY/MM formátum.
      id: 'expiryYear',
      card: 'trailing',
      header: t('paymentCards.columns.expiry'),
      sortable: true,
      cardLabel: t('paymentCards.columns.expiry'),
      width: 'minmax(0,1fr)',
      cell: (c) => {
        if (c.expiryYear === null || c.expiryMonth === null) return emDash;
        return (
          <span className="tabular-nums">
            {c.expiryYear}/{String(c.expiryMonth).padStart(2, '0')}
          </span>
        );
      },
    },
    {
      // 4. Ügyfél (feloldott név; rendezhető).
      id: 'clientName',
      header: t('paymentCards.columns.client'),
      sortable: true,
      cardLabel: t('paymentCards.columns.client'),
      width: 'minmax(0,1.25fr)',
      className: 'text-muted-foreground',
      cell: (c) => (c.clientName ? <span className="truncate">{c.clientName}</span> : emDash),
    },
    {
      // 5. HOZZÁRENDELÉSEK — a legacy lista 5. oszlopa: mely szolgáltatás-hozzárendelések fizetnek ezzel a
      //    kártyával. Nem dísz: csere/törlés előtt ez mondja meg, mi függ a kártyától. A legacy a SCOPE-ot is
      //    kiírta („ügyfél szintű" / „fiók szintű") + a tulajdonos nevét; üresnél „nincs hozzárendelés".
      id: 'assignments',
      card: 'subtitle',
      header: t('paymentCards.columns.assignments'),
      cardLabel: t('paymentCards.columns.assignments'),
      width: 'minmax(0,1.75fr)',
      multiline: true,
      cell: (c) =>
        c.assignments.length === 0 ? (
          <Text tone="tertiary" as="span">
            {t('paymentCards.noAssignments')}
          </Text>
        ) : (
          <ExpandableCell
            items={c.assignments}
            getKey={(a) => a.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('paymentCards.moreAssignments', { count: n })}
            triggerAriaLabel={t('paymentCards.moreAssignmentsAria')}
            renderItem={(a) => (
              <span className="block min-w-0">
                <span className="block truncate">{a.serviceName ?? emDashText}</span>
                <Text tone="tertiary" as="span" className="block truncate">
                  {`${t(`paymentCards.scopeLabel.${a.scope}`, { defaultValue: a.scope })}${
                    a.ownerName ? ` · ${a.ownerName}` : ''
                  }`}
                </Text>
              </span>
            )}
          />
        ),
    },
    {
      // 6. Státusz-badge — TISZTÁN számított a lejáratból (érvényes / hamarosan lejár / lejárt) — UTOLSÓ, dedikált oszlop.
      id: 'status',
      header: t('paymentCards.columns.status'),
      cardLabel: t('paymentCards.columns.status'),
      width: 'minmax(0,1fr)',
      cell: (c) => {
        const s = cardStatus(c, now);
        return (
          <DomainBadge kind="paymentCardStatus" value={s} label={t(`paymentCards.status.${s}`)} />
        );
      },
    },
  ];

  return (
    <AppShell
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('paymentCards.new'),
              onClick: () => setCreateOpen(true),
            }
          : undefined
      }
      toolbar={
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          filters={[
            {
              key: 'status',
              label: t('paymentCards.filterStatus'),
              value: statusFilter,
              onChange: (v: string) => setFilters({ status: v }),
              options: [
                { value: 'valid', label: t('paymentCards.status.valid') },
                { value: 'expiring', label: t('paymentCards.status.expiring') },
                { value: 'expired', label: t('paymentCards.status.expired') },
              ],
              allValue: ALL,
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('paymentCards.loading')
              : !isError && !infinite.isLoading
                ? t('paymentCards.countOf', { shown: rows.length, total })
                : ''
          }
          actions={
            <div className="hidden md:block">
              <ListNewButton
                label={t('paymentCards.new')}
                onClick={() => setCreateOpen(true)}
                enabled={canManage}
                disabledTooltip={t('common.noCreatePermission')}
              />
            </div>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<PaymentCardRow>
          columns={columns}
          rows={rows}
          getRowId={(c) => c.id}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('paymentCards.noMatches')}
                description={t('paymentCards.noMatchesDesc')}
              />
            ) : (
              <EmptyState
                title={t('paymentCards.empty')}
                description={t('paymentCards.emptyDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('paymentCards.columns.actions')}
          actionsColWidth={canManage && canDelete ? '7rem' : '5rem'}
          rowActions={(c) => (
            <PaymentCardRowActions
              row={c}
              canManage={canManage}
              canDelete={canDelete}
              onEdit={(row) => setEditTarget(row)}
              onDelete={(row) => setDeleteTarget(row)}
              t={t}
            />
          )}
        />
      </div>

      <PaymentCardFormSheet open={createOpen} onOpenChange={setCreateOpen} card={null} />
      <PaymentCardFormSheet
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
        card={editTarget}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('paymentCards.deleteTitle')}
        description={t('paymentCards.deleteDesc', {
          card: deleteTarget
            ? `${brandLabel(deleteTarget.brand)} •••• ${deleteTarget.last4 ?? ''}`
            : '',
        })}
        confirmLabel={t('paymentCards.delete')}
        cancelLabel={t('paymentCards.cancel')}
        pendingLabel={t('paymentCards.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
